import { Box, Button, FormControl, FormControlLabel, OutlinedInput, Select, TextField, Typography, MenuItem, Checkbox, CircularProgress, Paper, useTheme, Popover, Chip, FormHelperText, ListSubheader, IconButton, Dialog } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { Formik, Form, ErrorMessage, Field } from 'formik'
import { useNavigate, useSearchParams } from 'react-router-dom'
import InfoIcon from '@mui/icons-material/Info'
import React, { useEffect } from 'react'
import { toast } from 'react-toastify'
import { observer } from 'mobx-react'
import { useState } from 'react'
import * as Yup from 'yup'
import dayjs from 'dayjs'

import CustomSelectField from '../../../components/common/CustomSelectField'
import { formatDateTimeForDB } from '../../../utils/exam-utilities'
import CustomChip from '../../../components/common/CustomChip'
import { useUserStore } from '../../../store/user.store'
import { useExamStore } from '../../../store/exam.store'
import { Icons } from '../../../utils/utilities'
import api from '../../../service/api'

const HOURS = ["00", "01", "02", "03", "04", "05"]
const MINUTES = ["00", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55"]


const accessGroupsFilterFunc = ( item, values, filterEntity ) => {
    if ( filterEntity === "batch" ) {
        if ( item.entity !== 'batch' ) return false

        if ( !item.batch_assignment_id ) return false

        if ( +item.ac_year_id !== +values.academicYear ) return false

        if ( values.year && item.current_year !== values.year ) return false

        if ( values.sem && item.current_sem && values.sem !== item.current_sem ) return false

        return true
    } else {

        if ( item.entity !== 'section' ) return false

        if ( !item.section_assignment_id ) return false

        if ( +item.ac_year_id !== +values.academicYear ) return false

        if ( values.year && item.current_year !== values.year ) return false

        if ( item.current_sem && values.sem && values.sem !== item.current_sem ) return false

        return true
    }
}


const AddExam = observer( ( { open, setOpen } ) => {
    const initialDate = new Date().toISOString().split( 'T' )[0] + "T00:00"
    const supportedFormats = ["png", "jpg", "jpeg"]
    const attachmentsSupportedFormats = ["pdf", "docx", "doc", "xlsx"]

    const [loadingAcademicYears, setLoadingAcademicYears] = useState( true )
    const [isCreatingExam, setIsCreatingExam] = useState( false )
    const [startTimeData, setStartTimeData] = useState( new Date() )
    const [endDateTime, setEndDateTime] = useState( new Date() )
    const [refreshing, setRefreshing] = useState( false )
    const [isOpenExam, setIsOpenExam] = useState( true )
    const [openExamInfo, setOpenExamInfo] = useState( false )
    const [randomizeInfo, setRandomizeInfo] = useState( false )
    const [durationHours, setDurationHours] = useState( "00" )
    const [durationMinutes, setDurationMinutes] = useState( "00" )
    // const [sessionExpired, setSessionExpired] = useState( false )

    const UserStore = useUserStore()
    const ExamStore = useExamStore()

    const [academicYear, setAcademicYear] = useState( UserStore.getUserAcademicYears?.at( 0 )?.ac_year_id || "" )

    const [searchParams] = useSearchParams( { redirectTo: "" } )
    const navigate = useNavigate()

    const redirectTo = searchParams.get( 'redirectTo' ) || ""

    const { palette, border, components, layout } = useTheme()


    const initialValues = {
        title: "",
        description: "",
        topics: '',
        thumbnail: '',
        attachments: [],
        status: 'Draft',
        startDateTime: initialDate,
        endDateTime: initialDate,
        selectedBatches: [],
        duration: null,
        maximumAttempt: 1,
        subject: '',
        randomize: false,
        year: '',
        sem: '',
        examConstraints: {
            hide_scores: false
        }
    }

    const validationSchema = Yup.object().shape( {
        title: Yup.string().required( "Exam title is required." ).max( 250, "Max length 250 characters" ),
        description: Yup.string().required( "Exam description is required." ),
        topics: Yup.array().required( "Please provide the exam topics." ),
        thumbnail: Yup.mixed().nullable().notRequired().test( "FILE_FORMAT", "Unsupported file format, accepted formats: JPG,JPEG,PNG.", ( value ) => {
            if ( value )
                return supportedFormats.includes( value.name.split( "." ).pop().toLowerCase() )
            return true
        } ),
        startDateTime: Yup.date().required().min( new Date(), "Date is invalid" ),
        endDateTime: Yup.date().required().when( "startDateTime", ( startDate ) => {
            if ( startDate ) return Yup.date().min( startDate, "End date and time value must be greater than start date" )
        } ),

        attachments: Yup.mixed().nullable().notRequired().test( "ATTACHMENTS_FILE_FORMAT", "Unsupported attachment format! accepted formats: PDF.", ( value ) => {
            if ( value ) {
                const files = Object.values( value )
                for ( let i = 0; i < files.length; i++ ) {
                    const file = files[i]
                    if ( !attachmentsSupportedFormats.includes( file.name.split( "." ).pop().toLowerCase() ) ) {
                        return false
                    }
                }
                return true
            }
            else return true
        } ),

        duration: Yup.string().nullable().test( "CHECK_DURATION", "Invalid time format hours: HH and minutes: MM", ( value ) => {
            if ( isOpenExam ) {
                if ( value && value !== '00:00' ) return true
                return false
            } else return true
        } ),

        maximumAttempt: Yup.number().required( "This field is required!" ).min( 1, "At least 1 attempt must be given" ),
        subject: Yup.mixed().required( "Please select the subject." )
    } )

    const submit = async ( values, { resetForm } ) => {
        setIsCreatingExam( true )
        const formData = new FormData()
        formData.append( 'title', values.title )
        if ( values.thumbnail )
            formData.append( "thumbnail", values.thumbnail, values.thumbnail ? `${values.title}-thumb.${values.thumbnail.name.split( "." )[values.thumbnail.name.split( "." ).length - 1]}` : '' )
        formData.append( 'description', values.description )
        formData.append( 'topics', JSON.stringify( values.topics ) )
        formData.append( 'startDateTime', JSON.stringify( formatDateTimeForDB( values.startDateTime, 'en-IN', '-' ) ) )
        formData.append( 'endDateTime', JSON.stringify( formatDateTimeForDB( values.endDateTime, 'en-IN', '-' ) ) )
        formData.append( "status", values.status )
        formData.append( 'selectedBatches', JSON.stringify( values.selectedBatches ) )
        formData.append( 'duration', isOpenExam ? values.duration : null )
        formData.append( 'maximumAttempt', values.maximumAttempt )
        formData.append( "randomize", values.randomize )
        formData.append( "examSubject", JSON.stringify( values.subject ) )
        formData.append( "examConstraints", JSON.stringify( values.examConstraints ) )
        for ( let i = 0; i < values.attachments.length; i++ ) {
            formData.append( "attachments[]", values.attachments[i], `${values.title}-${Date.now()}${String( i + 1 )}.${values.attachments[i].name.split( "." )[values.attachments[i].name.split( "." ).length - 1]}` )
        }
        try {
            const { data } = await api.createExam( formData )
            navigate( `/faculty/exams/${data.data?.exam_id}` )
            await ExamStore.fetchExams()
            setOpen( false )
            toast.success( "New exam created successfully!" )
        } catch ( err ) {
            toast( err?.response?.data?.message || "An errorMessage occured while creating a new exam! try again later" )
        } finally {
            setIsCreatingExam( false )
        }
    }

    const handleSubjectChange = ( event, setFieldValue ) => {
        const subject = ( UserStore.getUserSubjectsDetails?.currentSubjects || [] ).find( s => s.unique_subject_key === event.target.value )
        setFieldValue( 'subject', subject )
        setFieldValue( 'year', subject.year )
        setFieldValue( 'sem', subject.sem )
        setFieldValue( 'academicYear', subject.ac_year_id )
    }

    const getBatches = async ( year = academicYear, refresh = false ) => {
        setRefreshing( "batches" )
        await UserStore.fetchUserBatchesAndSection( year, refresh )
        setRefreshing( null )
    }

    const refreshYears = async () => {
        setRefreshing( "years" )
        await UserStore.fetchUserAcademicYears( true )
        setRefreshing( null )
    }
    const removeSelectedAccessGroup = ( event, index, values, setFieldValue ) => {
        event.stopPropagation()
        const newSelectedIds = values.selected_access_groups || []
        const newSelectedGroups = values.selectedBatches || []
        newSelectedIds.splice( index, 1 )
        newSelectedGroups.splice( index, 1 )
        setFieldValue( 'selectedBatches', newSelectedIds )
        setFieldValue( 'selectedBatches', newSelectedGroups )
    }

    const refreshBatches = async () => {
        if ( academicYear )
            await getBatches( academicYear, true )
    }

    useEffect( () => {
        const getBatchesAndSectionsData = async () => {
            try {
                if ( UserStore.getErrorsStatuses?.academicYearsAndBatches )
                    await UserStore.fetchUsersAllBatchesAndSectionList()
                if ( UserStore.getErrorsStatuses?.academicYears )
                    await UserStore.fetchUserAcademicYears()
                if ( UserStore.getErrorsStatuses?.subjects === true )
                    await UserStore.fetchUserSubjects()
            } catch ( e ) {
                console.log( e )
                toast( "Something went wrong! try again later" )
            } finally {
                setLoadingAcademicYears( false )
            }
        }
        getBatchesAndSectionsData()
    }, [UserStore] )

    return (
        <Dialog maxWidth="md" open={open}  >

            <Box  >
                <Box position="sticky" top={0} zIndex={10} bgcolor={palette.background.paper} padding="10px 20px" borderBottom={border[1]} display="flex" gap={layout.sectionsGap} justifyContent="space-between" alignItems="center" >
                    <Box>
                        <Typography variant="h1" fontWeight="600" component="h1" fontSize="16px">Create new exam </Typography>
                        <Typography variant="subtitle2" fontSize="12px" color='textSecondary' >Create exam by providing all required details</Typography>
                    </Box>
                    <IconButton onClick={() => setOpen( false )}>
                        {Icons.default.CloseIcon}
                    </IconButton>
                </Box>
                <Box padding="20px" >
                    <Formik onSubmit={submit} initialValues={initialValues} validationSchema={validationSchema}>
                        {( { values, setFieldValue } ) => (
                            <Form method='POST' id='add-form' autoComplete='off' encType="multipart/form-data">

                                <FormControl fullWidth>
                                    <Typography component="label" htmlFor="title" gutterBottom variant="subtitle2" color={palette.form.labelColor}> Exam title * </Typography>
                                    <Field placeholder="Eg: Module 1 unit test" as={TextField} id="title" size="small" fullWidth name='title' />
                                    <FormHelperText ><ErrorMessage name='title' /></FormHelperText>
                                </FormControl>

                                <FormControl variant='outlined' margin="normal" fullWidth>
                                    <Typography component="label" htmlFor="description" gutterBottom variant="subtitle2" color={palette.form.labelColor}> Exam description * </Typography>
                                    <Field placeholder="Eg: Please answer all the question in all the sections." as={TextField} size="small" multiline rows={4} name='description' id='description' />
                                    <FormHelperText ><ErrorMessage name='description' /></FormHelperText>
                                </FormControl>

                                <FormControl variant='outlined' margin="normal" fullWidth>
                                    <Typography component="label" htmlFor="topics" gutterBottom variant="subtitle2" color={palette.form.labelColor}> Topics * </Typography>
                                    <Field
                                        placeholder="Eg: Module - 1, Module - 2"
                                        as={TextField} size="small" onChange={( e ) => { setFieldValue( 'topics', e.target.value.split( "," ) ) }}

                                        fullWidth
                                        name='topics'
                                        id='topics'
                                    />
                                    <FormHelperText ><ErrorMessage name='topics' /></FormHelperText>
                                </FormControl>

                                {values.topics.length > 0 && <Box display="flex" gap="5px" alignItems="center" flexWrap="wrap">
                                    {values.topics?.map( ( tag, index ) => {
                                        if ( tag?.trim() !== "" ) {
                                            return (
                                                <Chip deleteIcon={Icons.default.CloseIcon} key={index} sx={{ borderRadius: "5px", border: border[1], background: palette.contentBg }} label={tag} size="medium" />
                                            )
                                        }
                                        else return ""
                                    } )}
                                </Box>}


                                <FormControl fullWidth margin='normal'>
                                    <Box justifyContent="space-between" display="flex" gap="10px" alignItems="flex-end">
                                        <Typography component="label" htmlFor='academicYearInput' gutterBottom variant='subtitle2' color={palette.form.labelColor}>
                                            Select academic year *
                                        </Typography>
                                        <Button id="academicYearRefreshBtn" disabled={refreshing === "years"} startIcon={refreshing === "years" ? <CircularProgress size={14} /> : Icons.default.Sync} onClick={refreshYears} sx={{ width: "fit-content", flexShrink: 0, fontSize: "12px" }} color="secondary" >
                                            {refreshing === "years" ? "Refreshing academic years..." : "Refresh academic years"}
                                        </Button>
                                    </Box>
                                    <CustomSelectField itemValueKey={"ac_year_id"} renderOptionText={item => item.ac_year} data={UserStore.getUserAcademicYears} placeholder='Academic Year' size="small" onChange={event => setAcademicYear( event.target.value )} value={academicYear} />
                                </FormControl>


                                <FormControl margin='normal' variant='outlined' fullWidth>
                                    <Typography component="label" htmlFor='subject' gutterBottom variant="subtitle2" color={palette.form.labelColor}>Subject *</Typography>
                                    <CustomSelectField
                                        onChange={event => handleSubjectChange( event, setFieldValue )}
                                        renderValue={selected => {
                                            if ( selected ) {
                                                const subject = UserStore.getUserSubjectsDetails?.currentSubjects.find( sub => sub.unique_subject_key === selected )
                                                if ( subject )
                                                    return `${subject.subject_name?.toLowerCase()} (${subject.subject_code})`
                                                else
                                                    return "Subject"
                                            } else {
                                                return "Subject"
                                            }
                                        }}
                                        size="small"
                                        value={values.subject?.unique_subject_key || ""}
                                        data={( UserStore.getUserSubjectsDetails?.currentSubjects || [] )?.filter( item => item.ac_year_id === academicYear )}
                                        id="subject"
                                        itemValueKey="unique_subject_key"
                                        itemIdentityKey="unique_subject_key"
                                        renderOptionText={item => <Box display="flex" flexDirection="column">
                                            <Typography textTransform="capitalize" variant='subtitle2'>
                                                {item.subject_name?.toLowerCase()}
                                            </Typography>
                                            <Box display="flex" gap="5px" alignItems="center">
                                                <CustomChip tooltipTitle='Subject code' value={item.subject_code} />
                                                <CustomChip tooltipTitle='Year' value={`Year: ${item.year}`} />
                                                {item.sem && <CustomChip tooltipTitle='Semester' value={`Sem: ${item.sem}`} />}
                                            </Box>
                                        </Box>}
                                    />
                                    <FormHelperText ><ErrorMessage name='subject' /></FormHelperText>
                                </FormControl>

                                {values.subject?.subject_id && <FormControl margin='normal' sx={{ position: "relative" }} fullWidth >
                                    <Box justifyContent="space-between" display="flex" gap="10px" alignItems="flex-end">
                                        <Typography component="label" htmlFor='selectedBatches' gutterBottom variant="subtitle2" color={palette.form.labelColor}>Select the batches or sections</Typography>
                                        <Button disabled={refreshing === "batches" || !academicYear} startIcon={refreshing === "batches" ? <CircularProgress size={14} /> : Icons.default.Sync} onClick={refreshBatches} sx={{ width: "fit-content", marginTop: "2px", fontSize: "12px" }} color="secondary" >
                                            {refreshing === "batches" ? "Refreshing batches..." : "Refresh batches"}
                                        </Button>
                                    </Box>
                                    <CustomSelectField
                                        onChange={( e ) => {
                                            const value = typeof e.target.value === 'string' ? e.target.value.split( "," ) : e.target.value
                                            setFieldValue( 'selectedBatches', value )
                                        }}
                                        options={{ showCheckbox: true }}
                                        slotProps={{
                                            input: {
                                                sx: {
                                                    padding: values.selectedBatches?.length > 0 ? "5px" : ""
                                                }
                                            }
                                        }}
                                        searchFilter={( item, searchTerm ) => {
                                            return item.short_title?.toLowerCase()?.includes( searchTerm?.toLowerCase() )
                                        }}
                                        noOptionsMessage="We couldn't find any sections or batches for the year and semester of the selected subject."
                                        renderValue={value => {
                                            if ( value?.length === 0 ) {
                                                return "Select batches and sections."
                                            } else {
                                                return <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: "5px" }}>
                                                    {values.selectedBatches.map( ( agId, index ) => {

                                                        const ag = UserStore.getUsersAllBatchesAndSectionList?.find( item => item.id === agId )

                                                        return (
                                                            <Box padding="2px 5px" bgcolor={palette.contentBg} border={border[1]} borderRadius="10px" key={ag?.id} display="flex" alignItems="center">
                                                                <Typography fontSize="12px" variant='subtitle2'>
                                                                    {ag?.chip_label}
                                                                </Typography>
                                                                <IconButton onMouseDown={e => e.stopPropagation()} onClick={event => removeSelectedAccessGroup( event, index, values, setFieldValue )} size='small' color='error'>
                                                                    {Icons.default.CancelIcon}
                                                                </IconButton>
                                                            </Box>
                                                        )
                                                    } )}
                                                </Box>
                                            }
                                        }}
                                        size="small"
                                        value={values.selectedBatches || ""}
                                        data={{
                                            sections: UserStore.getUsersAllBatchesAndSectionList?.filter( item => accessGroupsFilterFunc( item, values, "section" ) ),
                                            batches: UserStore.getUsersAllBatchesAndSectionList?.filter( item => accessGroupsFilterFunc( item, values, "batch" ) )
                                        }}
                                        id="batchesAndSectionsInput"
                                        itemValueKey="id"
                                        multiple
                                        renderOptionText={item => <Box display="flex" flexDirection="column">
                                            <Typography variant='subtitle2'>
                                                {item.short_title}
                                            </Typography>
                                            <Box display="flex" gap="5px" alignItems="center">
                                                <CustomChip tooltipTitle='Course short name' value={item.course_name_short} />
                                                <CustomChip tooltipTitle='Year' value={`Year: ${item.current_year}`} />
                                                {item.current_year && <CustomChip tooltipTitle='Semester' value={`Sem: ${item.current_sem}`} />}
                                            </Box>
                                        </Box>}
                                    />

                                </FormControl>}

                                <FormControl fullWidth margin="normal" sx={{ maxWidth: "250px" }}>
                                    <Typography component="label" htmlFor='maximumattempt' gutterBottom variant="subtitle2" color={palette.form.labelColor}>Maximum attempts *</Typography>
                                    <Field as={TextField} id="maximumattempt" size="small" name='maximumAttempt' />
                                    <FormHelperText><ErrorMessage name='maximumAttempt' /></FormHelperText>
                                </FormControl>

                                <FormControl margin='normal' sx={{ display: "flex", maxWidth: "250px" }}>
                                    <Box >
                                        <Typography component="label" htmlFor='startdatetime' display="block" gutterBottom variant="subtitle2" color={palette.form.labelColor}>Start date and time *</Typography>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DateTimePicker
                                                {...( components?.MuiPickerOutlinedInput?.defaultProps || {} )}
                                                id="startdatetime"
                                                sx={{ width: "100%", fontSize: "14px", '& input': { padding: "10px" } }}
                                                minDateTime={dayjs( new Date() )}
                                                value={dayjs( startTimeData )}
                                                format="DD-MM-YYYY hh:mm A"
                                                onChange={( e ) => { if ( e.$d !== "Invalid Date" ) { setStartTimeData( e ); setFieldValue( 'startDateTime', e ) } }}

                                            />
                                        </LocalizationProvider>
                                    </Box>
                                    <Typography variant="subtitle2" fontSize="12px" color="textSecondary">Enter start date and time.</Typography>
                                    <FormHelperText ><ErrorMessage name='startDateTime' /></FormHelperText>

                                </FormControl>
                                <FormControl margin="normal" sx={{ maxWidth: "250px", display: "flex" }}>
                                    <Box>
                                        <Typography component="label" htmlFor='startdatetime' display="block" gutterBottom variant="subtitle2" color={palette.form.labelColor}>End date and time *</Typography>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DateTimePicker
                                                {...( components?.MuiPickerOutlinedInput?.defaultProps || {} )}
                                                id="enddatetime"
                                                sx={{ width: "100%", fontSize: "14px", '& input': { padding: "10px" } }}
                                                value={dayjs( endDateTime )}
                                                format="DD-MM-YYYY hh:mm A"
                                                onChange={( e ) => { setEndDateTime( e ); setFieldValue( 'endDateTime', e ) }}
                                            />
                                        </LocalizationProvider>
                                    </Box>
                                    <Typography variant="subtitle2" fontSize="12px" color="textSecondary">Enter end date and time.</Typography>
                                    <FormHelperText><ErrorMessage name='endDateTime' /></FormHelperText>
                                </FormControl>

                                {isOpenExam && <FormControl variant='outlined' margin='normal' sx={{ display: "flex" }}>
                                    <Typography variant='subtitle2' color={palette.form.labelColor} gutterBottom>Exam duration *</Typography>
                                    <Box display="flex" flexWrap="wrap" gap="10px" alignItems="center">
                                        <FormControl sx={{ maxWidth: "250px" }} fullWidth>
                                            <CustomSelectField size="small" value={durationHours} onChange={e => { setDurationHours( e.target.value ); setFieldValue( 'duration', `${e.target.value}:${durationMinutes}` ) }} data={HOURS} endAdornment={<Typography fontSize="12px" marginRight="20px" sx={{ pointerEvents: "none" }} color="textSecondary">HOURS</Typography>} />
                                        </FormControl >
                                        <FormControl sx={{ maxWidth: "250px" }} fullWidth>
                                            <CustomSelectField size="small" value={durationMinutes} onChange={e => { setDurationMinutes( e.target.value ); setFieldValue( 'duration', `${durationHours}:${e.target.value}` ) }} data={MINUTES} endAdornment={<Typography fontSize="12px" marginRight="20px" sx={{ pointerEvents: "none" }} color="textSecondary">MIN</Typography>} />
                                        </FormControl>
                                    </Box>

                                    <Typography color='textSecondary' fontSize="12px" marginTop="2px" variant="subtitle2" >Exam duration: {`${durationHours ? durationHours : 0} hours ${durationMinutes ? durationMinutes : 0} minutes`}</Typography>
                                    <FormHelperText  ><ErrorMessage name='duration' /></FormHelperText>
                                </FormControl>}


                                {<Box position="relative">
                                    {<Popover slotProps={{ paper: { sx: { maxWidth: "300px" } } }} anchorEl={openExamInfo} onClose={e => setOpenExamInfo( null )} open={Boolean( openExamInfo )} >
                                        <Typography padding="10px" borderBottom="1px solid #d3d3d3" variant="subtitle2" color="secondary">Stretched Exams</Typography>
                                        <Typography padding="10px" textAlign="justify" variant='body2'>In stretched exams the the exam countdown once started by a student, ends at the exam's end date and time as opposed to the duration exam where the countdown ends when the duration set by the faculty ends.</Typography>
                                    </Popover>}
                                    <Box display="flex" alignItems="center">
                                        <FormControlLabel control={<Checkbox color='primaryDark' value={!isOpenExam} onChange={e => { setIsOpenExam( !e.target.checked ) }} />} label="Stretch exam from start to end time?" />
                                        <InfoIcon sx={{ cursor: "pointer" }} onClick={e => setOpenExamInfo( e.currentTarget )} />
                                    </Box>
                                </Box>}

                                {<Box position="relative">
                                    <Popover open={Boolean( randomizeInfo )} onClose={e => setRandomizeInfo( null )} anchorEl={randomizeInfo} slotProps={{ paper: { sx: { maxWidth: "300px" } } }}>
                                        <Typography padding="10px" borderBottom="1px solid #d3d3d3" variant="subtitle2" color="secondary">Randomize questions and options?</Typography>
                                        <Typography textAlign="justify" padding="10px" variant='body2'>If selected the questions and their options will be in random order for each individual taking the exam.</Typography>
                                    </Popover>
                                    <Box display="flex" alignItems="center">
                                        <FormControlLabel control={<Checkbox color='primaryDark' value={values.randomize} onChange={e => { setFieldValue( 'randomize', e.target.checked ) }} />} label="Randamize questions and options?" />
                                        <InfoIcon sx={{ cursor: "pointer" }} onClick={e => setRandomizeInfo( e.currentTarget )} />
                                    </Box>
                                </Box>}
                                {<Box display="flex" alignItems="center">
                                    <FormControlLabel control={<Checkbox color='primaryDark' value={values.examConstraints?.hide_scores} onChange={e => { setFieldValue( 'examConstraints.hide_scores', e.target.checked ) }} />} label="Hide scores from students." />
                                </Box>}
                                <Box display="flex" gap={layout.cardsGap} marginTop="40px">
                                    <Button disableElevation disabled={isCreatingExam} type='submit' startIcon={isCreatingExam ? <CircularProgress size={14} /> : Icons.default.AddIcon} variant='contained' sx={{ textTransform: "capitalize" }}>{isCreatingExam ? "Creating exam..." : "Create Exam"}</Button>
                                    <Button disableElevation color="error" onClick={() => setOpen( false )} startIcon={Icons.default.CloseIcon} disabled={isCreatingExam} variant='outlined' sx={{ textTransform: "capitalize" }}>Cancel</Button>
                                </Box>
                            </Form>
                        )}
                    </Formik>
                </Box>
            </Box>
        </Dialog>
    )
}

)

export default AddExam